import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useLabels, useFlags, usePrefs, useRequestData } from 'wsm-common-data';
import { hysteriFetch, wrappers } from 'hysterics';
import { setNewRelicCustomAttribute } from 'ddc-new-relic';
import { trackEvent } from 'ddc-track-event';
import logger from 'ws-scripts/modules/logger';
import { validateWindowStickerCondition } from '../../../utilities/validateWindowStickerCondition';
import Spinner from './Spinner';
import { getVehicleObj } from '../../../utilities/vehicleTracking';
import vehiclePropType from '../../../propTypes/vehiclePropType';

const fetchData = hysteriFetch('api-data');

const WindowSticker = ({ vehicle }) => {
	const labels = useLabels();
	const flags = useFlags();
	const { showSRPWindowSticker } = usePrefs();
	const { widgetName, windowId, locale } = useRequestData();
	const { franchiseList } = useSelector((state) => state.franchiseList);
	const { accounts } = useSelector((state) => state.accountData);

	const [isValid, setIsValid] = useState(true);
	const [isLoading, setIsLoading] = useState(false);

	if (
		!validateWindowStickerCondition(
			vehicle,
			showSRPWindowSticker,
			franchiseList,
			accounts
		)
	) {
		return null;
	}

	const vehicleObj = {
		make: vehicle.make,
		vin: vehicle.vin,
		type: vehicle.type,
		year: vehicle.year,
		flags,
		locale,
		accountId: vehicle.accountId,
		certified: vehicle.certified,
		extraFranchises: '',
		account: { ...accounts[vehicle.accountId], franchises: franchiseList }
	};

	const handleWindowSticker = async () => {
		setIsLoading(true);

		const pubsub = window?.DDC?.pubsub;
		if (pubsub) {
			pubsub.publish('wsm-vehicle-toast/interacted-vehicles', {
				interaction: 'ddc-window-sticker-opened',
				timestamp: Date.now(),
				uuid: vehicle.uuid,
				widgetName: 'ws-inv-listing'
			});
		}

		try {
			const response = await fetchData(
				'/api/widget/ws-inv-listing/getwindowstickerurl',
				{
					method: 'POST',
					headers: {
						'Content-Type': 'application/json'
					},
					[wrappers.retry.contextKey]: {
						retries: flags['ws-vehicle-ctas-window-sticker-retries']
					},
					[wrappers.timeout.contextKey]: {
						timeout: flags['ws-vehicle-ctas-window-sticker-timeout']
					},
					body: JSON.stringify(vehicleObj)
				}
			);

			const responseData = await response.data;

			if (responseData) {
				trackEvent(widgetName, windowId, {
					action: 'clicked',
					element: 'CTA Button/Link',
					elementCTA: labels.get('VIEW_WINDOW_STICKER'),
					event: 'widgetUserEvent',
					result: 'Navigated to CTA destination',
					widgetState: 'default',
					dataToExcludeFromWidgetUserEvent: {
						url: responseData || '',
						isWindowStickerLink: true,
						vehicle
					},
					vehicle: getVehicleObj(vehicle.uuid)
				});

				setIsValid(true);
				window.open(responseData, '_blank').focus();
			} else {
				setIsValid(false);
			}
		} catch (e) {
			setIsValid(false);
			const errorString = JSON.stringify(e);
			setNewRelicCustomAttribute(
				'srpWindowStickerFetchError',
				errorString
			);
			logger.error(`Window sticker fetch Error: ${errorString}`);
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<button
			className="btn-block btn-sm btn-primary btn mb-3 window-sticker-link"
			onClick={handleWindowSticker}
			aria-disabled={isLoading || !isValid ? 'true' : 'false'}
			disabled={isLoading || !isValid}
			type="button"
		>
			{isLoading && (
				<>
					<Spinner /> {labels.get('LOADING')}
				</>
			)}
			{isValid && !isLoading && labels.get('VIEW_WINDOW_STICKER')}
			{!isValid && labels.get('WINDOW_STICKER_NOT_AVAILABLE')}
		</button>
	);
};

export default WindowSticker;

WindowSticker.propTypes = {
	vehicle: vehiclePropType.isRequired
};
